import React from 'react';
import { Controller } from 'react-hook-form';
import type { Control, FieldErrors, UseFormRegister, UseFormSetValue } from 'react-hook-form';
import { AutocompleteSelect } from '../../components/common/AutocompleteSelect';
import { formatCurrency } from '../../utils/format';
import { openNativeDatePickerSafely, preventManualDateEntry, preventManualDatePasteOrDrop } from '../../utils/date';
import type { SalesOrderFormData, SalesOrder } from '../../types/salesOrder';
import type { Customer } from '../../types/customer';
import type { AutocompleteOption } from '../../components/common/AutocompleteSelect';
import { useLanguage } from '../../features/language/useLanguage';

interface Props {
  control: Control<SalesOrderFormData>;
  register: UseFormRegister<SalesOrderFormData>;
  setValue: UseFormSetValue<SalesOrderFormData>;
  errors: FieldErrors<SalesOrderFormData>;
  customers: Customer[];
  customerOptions: AutocompleteOption[];
  areFieldsDisabled: boolean;
  balanceDue: number;
  currencySymbol: string;
  initialData?: SalesOrderFormData;
  onCreateCustomer?: (name: string) => void;
}

export const SalesOrderHeaderSection: React.FC<Props> = ({
  control,
  register,
  setValue,
  errors,
  customers,
  customerOptions,
  areFieldsDisabled,
  balanceDue,
  currencySymbol,
  initialData,
  onCreateCustomer
}) => {
  const { t } = useLanguage();
  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <div className="flex-1 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-1">
            <Controller
              control={control}
              name="customer"
              render={({ field }) => (
                <AutocompleteSelect
                  disabled={areFieldsDisabled}
                  dataTestId="so-customer-input"
                  label={t('salesOrder.formHeader.customer')}
                  value={field.value}
                  onChange={(text, option) => {
                    field.onChange(option ? option.text : text);
                    if (option) {
                      const customer = customers.find(c => c.id === option.id);
                      setValue('customerEmail', customer?.email || '');
                      setValue('billingAddress', customer?.billingAddress || '');
                      setValue('shippingAddress', customer?.shippingAddress || customer?.billingAddress || '');
                      setValue('messageOnInvoice', customer?.messageOnInvoice || '');
                    } else {
                      setValue('customerEmail', '');
                    }
                  }}
                  onCreateOption={(name) => onCreateCustomer?.(name)}
                  options={customerOptions}
                  error={errors.customer?.message}
                  placeholder={t('salesOrder.formHeader.customerPlaceholder')}
                  allowCustom
                />
              )}
            />
          </div>
          <div className="md:col-span-1">
            <label className="block text-sm font-medium text-gray-700">{t('salesOrder.formHeader.customerEmail')}</label>
            <input
              disabled={true}
              data-test-id="so-customer-email"
              type="email"
              {...register('customerEmail')}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">{t('salesOrder.formHeader.orderDate')}</label>
              <input
                disabled={areFieldsDisabled}
                data-test-id="so-create-order-date"
                type="date"
                {...register('orderDate')}
                onClick={openNativeDatePickerSafely}
                onKeyDown={preventManualDateEntry}
                onPaste={preventManualDatePasteOrDrop}
                onDrop={preventManualDatePasteOrDrop}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
              />
              {errors.orderDate && <p className="mt-1 text-sm text-red-600">{errors.orderDate.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">{t('salesOrder.formHeader.terms')}</label>
              <select
                disabled={areFieldsDisabled}
                data-test-id="so-terms"
                {...register('terms')}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
              >
                <option value="">{t('salesOrder.formHeader.selectTerms')}</option>
                <option value="Net 7">{t('salesOrder.formHeader.net7')}</option>
                <option value="Net 14">{t('salesOrder.formHeader.net14')}</option>
                <option value="Net 30">{t('salesOrder.formHeader.net30')}</option>
                <option value="Due on receipt">{t('salesOrder.formHeader.dueOnReceipt')}</option>
              </select>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">{t('salesOrder.formHeader.invoiceDate')}</label>
              <input
                disabled={areFieldsDisabled}
                data-test-id="so-invoice-date"
                type="date"
                {...register('invoiceDate')}
                onClick={openNativeDatePickerSafely}
                onKeyDown={preventManualDateEntry}
                onPaste={preventManualDatePasteOrDrop}
                onDrop={preventManualDatePasteOrDrop}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">{t('salesOrder.formHeader.dueDate')}</label>
              <input
                disabled={areFieldsDisabled}
                data-test-id="so-due-date"
                type="date"
                {...register('dueDate')}
                onClick={openNativeDatePickerSafely}
                onKeyDown={preventManualDateEntry}
                onPaste={preventManualDatePasteOrDrop}
                onDrop={preventManualDatePasteOrDrop}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="w-full lg:w-80 xl:w-96 bg-gray-50 border border-gray-200 rounded-lg p-4 flex flex-col justify-between">
        <div className="flex flex-col items-start gap-2">
          <span className="text-xs font-semibold tracking-wide text-gray-500">{t('salesOrder.formHeader.balanceDue')}</span>
          <span
            data-test-id="so-balance-due-header"
            className="text-2xl font-bold text-gray-900"
          >
            {formatCurrency(balanceDue, currencySymbol)}
          </span>
        </div>
        <div className="mt-4 space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-500">{t('salesOrder.formHeader.invoiceNo')}</label>
            <input
              data-test-id="so-invoice-number"
              type="text"
              readOnly
              value={initialData && 'orderNumber' in initialData ? (initialData as SalesOrder).orderNumber : ''}
              className="mt-1 block w-full rounded-md border-gray-200 bg-gray-100 shadow-sm sm:text-sm border p-2 text-gray-700"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500">{t('salesOrder.formHeader.store')}</label>
            <input
              disabled={areFieldsDisabled}
              data-test-id="so-store"
              type="text"
              {...register('store')}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm border p-2"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
